import { FieldConfigs, PostalCodeLocale, ValidatorFieldState, ValidatorFn } from '@frontend/design-system';
import { CHARACTER_LIMIT_ERROR, MAX_LENGTH_TEXTAREA } from '../constants';

export const maxLengthValidator =
  (maxLength: number = MAX_LENGTH_TEXTAREA): ValidatorFn<'text'> =>
  ({ value }: ValidatorFieldState<'text'>) => {
    if (value && value.length > maxLength) {
      return CHARACTER_LIMIT_ERROR(maxLength);
    }
    return '';
  };

export const textField = (props?: Omit<FieldConfigs['text'], 'type'>): FieldConfigs['text'] => ({
  type: 'text' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  ...props,
});

export const multiSelect = (props?: Omit<FieldConfigs['multiselect'], 'type'>): FieldConfigs['multiselect'] => ({
  type: 'multiselect' as const,
  required: props?.required ?? false,
  value: props?.value ?? [],
  ...props,
});

export const listField = (props?: Omit<FieldConfigs['list'], 'type'>): FieldConfigs['list'] => ({
  type: 'list' as const,
  required: props?.required ?? false,
  value: props?.value ?? [],
  ...props,
});

export const switchField = (props?: Omit<FieldConfigs['switch'], 'type'>): FieldConfigs['switch'] => ({
  type: 'switch' as const,
  required: props?.required ?? false,
  value: props?.value ?? false,
  ...props,
});

export const dropdownField = (props?: Omit<FieldConfigs['dropdown'], 'type'>): FieldConfigs['dropdown'] => ({
  type: 'dropdown' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  ...props,
});

export const postalCodeField = (props?: Omit<FieldConfigs['postalCode'], 'type'>): FieldConfigs['postalCode'] => ({
  type: 'postalCode' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  locale: props?.locale ?? PostalCodeLocale.US,
  ...props,
});

export const radioField = (props?: Omit<FieldConfigs['radio'], 'type'>): FieldConfigs['radio'] => ({
  type: 'radio' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  ...props,
});

export const phoneField = (props?: Omit<FieldConfigs['phone'], 'type'>): FieldConfigs['phone'] => ({
  type: 'phone' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  ...props,
});

export const emailField = (props?: Omit<FieldConfigs['email'], 'type'>): FieldConfigs['email'] => ({
  type: 'email' as const,
  required: props?.required ?? false,
  value: props?.value ?? '',
  ...props,
});

export const textareaField = (
  props?: Omit<FieldConfigs['text'], 'type'>,
  maxLength: number = MAX_LENGTH_TEXTAREA
): FieldConfigs['text'] => {
  const defaultValidator = maxLengthValidator(maxLength);
  const validator = props?.validator
    ? (fieldState: ValidatorFieldState<'text'>, allFields?: any) => {
        const lengthError = defaultValidator(fieldState, allFields);
        if (lengthError) return lengthError;
        return props.validator?.(fieldState, allFields) ?? '';
      }
    : defaultValidator;

  return {
    type: 'text' as const,
    required: props?.required ?? false,
    value: props?.value ?? '',
    validateOnChange: true,
    ...props,
    validator,
  };
};

export const checklistField = (props?: Omit<FieldConfigs['checklist'], 'type'>): FieldConfigs['checklist'] => ({
  type: 'checklist' as const,
  required: props?.required ?? false,
  value: props?.value ?? [],
  ...props,
});
